/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 31/10/2023
 */

import { Backdrop, CircularProgress, Container } from "@mui/material";
import React, { useEffect, useReducer, useState } from "react";
import DetailsStep from "../Components/Checkout/DetailsStep";
import AvailabilityStep from "../Components/Checkout/AvailabilityStep";
import PaymentStep from "../Components/Checkout/PaymentStep";
import CheckoutContext from "../Context/CheckoutContext";
import CheckoutSteps from "../Components/Checkout/CheckoutSteps";
import NavigationButtons from "../Components/Checkout/NavigationButtons";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AdditionalDetailsStep from "../Components/Checkout/AdditionalDetailsStep";
import NavBar from "../Components/NavBar/NavBar";
import SelectAddress from "../Components/Checkout/SelectAddress";
import { Helmet } from "react-helmet";
import {
  useGetCategoryByIdQuery,
  useGetSubCategoriesByIdQuery,
} from "../Store/Services/CategoryService";
import ProceedDialog from "../Components/Auth/proceedDialog";
import GuestDetails from "../Components/Checkout/GuestDetails";
import LoginDialog from "../Components/Login/LoginDialog";
import { useLazyGetResourceByAreaDateTimeQuery } from "../Store/Services/ResourceService";

const steps = ["Details", "Other Details", "Availability", "Payment"];

const init = (initialState) => {
  return initialState;
};

export const SET_SUB_CATEGORY = "SET_SUB_CATEGORY";
export const SET_FREQUENCY = "SET_FREQUENCY";
export const SET_COMMENTS = "SET_COMMENTS";
export const SET_HOURS = "SET_HOURS";
export const SET_NO_OF_PROFESSIONALS = "SET_NO_OF_PROFESSIONALS";
export const SET_MATERIALS_NEEDED = "SET_MATERIALS_NEEDED";
export const SET_RESOURCE = "SET_RESOURCE";
export const SET_DATE = "SET_DATE";
export const SET_TIME = "SET_TIME";
export const SET_ADDRESS_DETAILS = "SET_ADDRESS_DETAILS";
export const SET_TOTAL = "SET_TOTAL";
export const SET_PAYMENT_METHOD = "SET_PAYMENT_METHOD";
export const SET_IMAGES = "SET_IMAGES";

const reducer = (state, action) => {
  switch (action.type) {
    case SET_SUB_CATEGORY:
      return { ...state, ...action.payload };
    case SET_FREQUENCY:
      return { ...state, freqId: action.payload };
    case SET_COMMENTS:
      return { ...state, remarks: action.payload };
    case SET_HOURS:
      return { ...state, noHrs: action.payload };
    case SET_NO_OF_PROFESSIONALS:
      return { ...state, noRes: action.payload };
    case SET_MATERIALS_NEEDED:
      return { ...state, isMaterialsNeeded: action.payload };
    case SET_RESOURCE:
      return { ...state, resourceId: action.payload };
    case SET_DATE:
      return { ...state, ...action.payload };
    case SET_TIME:
      return { ...state, ...action.payload };
    case SET_ADDRESS_DETAILS:
      return { ...state, ...action.payload };
    case SET_TOTAL:
      return { ...state, amount: action.payload };
    case SET_PAYMENT_METHOD:
      return { ...state, paymentMethodId: action.payload };
    case SET_IMAGES:
      return { ...state, images: action.payload };
  }
};

const initialState = {
  catId: null,
  freqId: null,
  remarks: "",
  noHrs: 0,
  noRes: 0,
  isMaterialsNeeded: 0,
  resourceId: 0,
  dateId: null,
  date: null,
  timeSlotId: null,
  timeSlot: null,
  amount: null,
  paymentMethodId: null,
  images: [],
};

const CheckoutPage = () => {
  const [checkoutData, dispatch] = useReducer(reducer, initialState, init);

  const navigate = useNavigate();
  const location = useLocation();

  const catId = location.search.slice(4);

  const [isDisabled, setIsDisabled] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [open, setOpen] = useState(false);
  const [openAuthDialog, setOpenAuthDialog] = useState(false);
  const [openProceedDialog, setOpenProceedDialog] = useState(false);
  const [openGuestDetails, setOpenGuestDetails] = useState(false);

  const isSignedIn = sessionStorage.getItem("isSignedIn")
  const [getProfs, { data }] = useLazyGetResourceByAreaDateTimeQuery();
  const [professionals, setProfessionals] = useState([])

  const getResources = async () => {
    
    try {
      const profs = await getProfs({
        areaId: checkoutData.areaId,
        calId: checkoutData.dateId,
        slotId: checkoutData.timeSlotId,
        catId: catId
      }).unwrap();
      //console.log("API response:", profs);
      setProfessionals(profs);
    } catch (error) {
      //console.error("API error:", error);
      setProfessionals([]);
      onResourceSelect(0);
    }
  };

  const onResourceSelect = (id) => {
    dispatch({ type: SET_RESOURCE, payload: id })
  }
  useEffect(() => {
    if (checkoutData.areaId && checkoutData.dateId && checkoutData.timeSlotId) {
      getResources();
    }
  }, [checkoutData.areaId, checkoutData.dateId, checkoutData.timeSlotId]);

  const {
    data: categories,
    isLoading,
    isSuccess,
  } = useGetSubCategoriesByIdQuery(catId);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (activeStep === 2) {
      isSignedIn == 1 && setOpen(true);
      if (!isSignedIn || isSignedIn == 0) {
        setOpenProceedDialog(true);
      }
    }
  }, [activeStep, isSignedIn]);

  const renderStep = (step) => {
    switch (step) {
      case 0:
        return (
          (!isLoading || isSuccess) && (
            <DetailsStep
              categories={categories}
              setIsDisabled={setIsDisabled}
            />
          )
        );
      case 1:
        return (
          (!isLoading || isSuccess) && (
            <AdditionalDetailsStep
              categories={categories}
              setIsDisabled={setIsDisabled}
            />
          )
        );
      case 2:
        return <AvailabilityStep catId={catId} setIsDisabled={setIsDisabled} />;
      case 3:
        return <PaymentStep />;
      default:
        break;
    }
  };

  const handleNext = () => {
    setActiveStep((currentStep) => currentStep + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate("/");
    } else {
      setActiveStep((currentStep) => currentStep - 1);
    }
  };

  const onCloseDialog = () => {
    setOpen(false);
    getResources();
  };

  const onProceedWithoutLogin = () => {
    // setOpenProceedDialog(false);
    setOpenGuestDetails(true);
  };

  const handleOpenAuthDialog = () => {
    setOpenAuthDialog(true);
  };

  const parentCategoryId = categories?.[0]?.parentCat;

  const {
    data: categoryData,
    error: categoryError,
    isSuccess: isCategorySuccess,
  } = useGetCategoryByIdQuery(parentCategoryId, {
    skip: !parentCategoryId, // ✅ Skip API call if parentCategoryId is undefined
  });

  const [images, setImages] = useState([]);

  useEffect(() => {
    if (isCategorySuccess && categoryData && categoryData.imagesList) {
      const imageUrls = categoryData.imagesList.split(",");
      setImages(imageUrls);
    }
  }, [isCategorySuccess, categoryData]);

  const addCategoryJsonLd = () => {
    if (!categoryData) return null;

    return {
      __html: JSON.stringify({
        "@context": "https://schema.org/",
        "@type": "Category",
        name: categoryData.catDisplay,
        description: categoryData.catDesc,
        image: images.map(
          (image) => `${process.env.REACT_APP_IMAGE_URL}${image}`
        ),
        // "keywords": categoryData.seoKeyword
        //   ? categoryData.seoKeyword.split(",").map((keyword) => keyword.trim())
        //   : [],
      }),
    };
  };

  return (
    <>
      <Helmet>
        <title>{categoryData?.catName}</title>
        <meta name="description" content={categoryData?.catDesc} />
        <meta property="og:title" content={categoryData?.catName} />
        <meta property="og:description" content={categoryData?.catDesc} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={addCategoryJsonLd()}
        />
        <link
          rel="canonical"
          href={`${process.env.prodUrl
            }/checkout/${window.location.pathname.slice(10)}?id=${catId}`}
          key="canonical"
        />
      </Helmet>
      <NavBar />
      <Container style={{ margin: "1rem auto", maxWidth: "1500px" }}>
        {isLoading && (
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={isLoading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
        <CheckoutSteps activeStep={activeStep} steps={steps} />
        <CheckoutContext.Provider
          value={{ checkoutData, dispatch, handleNext, handleBack }}
        >
          <NavigationButtons step={activeStep} disabled={isDisabled} />
          {renderStep(activeStep)}
          <SelectAddress
            open={isSignedIn == 1 && open}
            onClose={onCloseDialog}
          />
          <ProceedDialog
            open={!isSignedIn && openProceedDialog}
            onClose={() => setOpenProceedDialog(false)}
            onProceedWithoutLogin={onProceedWithoutLogin}
          />

          <LoginDialog
            onCloseFinal={() => {
              setOpen(true);
              setOpenProceedDialog(false);
            }}
          />
          <GuestDetails
            open={openGuestDetails}
            handleClose={() => {
              setOpenGuestDetails(false);
              if (sessionStorage.getItem("isSignedIn") === "0") {
                setOpenProceedDialog(true);
              }
            }}
            onCloseFinal={() => {
              setOpenProceedDialog(false);
              setOpen(true);
            }}
          />
        </CheckoutContext.Provider>
      </Container>
    </>
  );
};

export default CheckoutPage;
